import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { TbCopy } from "react-icons/tb";
import { RevenueCommissions } from "./RevenueCommissions";
import { WeeklyGoal } from "./WeeklyGoal";
import { getCouponAffiliateStats } from "../../api/Analytics/analyticsServices";
import type { ICouponAffiliateStats } from "../../api/Analytics/analyticsModels";
import { getUserCurrency } from "../../api/profile/profileServices";
import { getCurrencySymbol } from "../../utils/currency/getCurrencySymbol";

// ── Copy card (code / utm_source) ───────────────────────────────────────────

function CopyCard({
  label,
  value,
  textToCopy,
}: {
  label: string;
  value: string;
  textToCopy?: string;
}) {
  const { t } = useTranslation();

  const copy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(textToCopy);
      toast.success(t("affiliate.copied", { label }));
    } catch {
      toast.error(t("affiliate.copyFailed"));
    }
  };

  return (
    <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs text-gray-400 font-medium mb-1">{label}</p>
          <p className="text-lg font-bold text-gray-800 truncate">
            {value || "—"}
          </p>
        </div>
        <button
          type="button"
          onClick={copy}
          disabled={!value}
          aria-label={t("affiliate.copy", { label })}
          className="w-9 h-9 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 hover:text-gray-900 transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <TbCopy className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Affiliate() {
  const { t } = useTranslation();
  const [data, setData] = useState<ICouponAffiliateStats | null>(null);
  const [currencySymbol, setCurrencySymbol] = useState("$");

  useEffect(() => {
    getUserCurrency()
      .then((res) => setCurrencySymbol(getCurrencySymbol(res.data?.currency)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    getCouponAffiliateStats()
      .then((res) => {
        // Backend returns an array of coupon stats; use the first entry.
        const payload = res.data;
        setData(
          Array.isArray(payload) ? (payload[0] ?? null) : (payload ?? null),
        );
      })
      .catch(() => {});
  }, []);

  const charts = data?.charts;

  return (
    <div className="p-4 sm:p-6 bg-[#f9f9f9] min-h-screen flex flex-col gap-5">
      {/* Coupon code + utm_source (copyable) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
        <CopyCard
          label={t("affiliate.couponCode")}
          value={data?.code ?? ""}
          textToCopy={data?.code ?? ""}
        />
        <CopyCard
          label={t("affiliate.utmSource")}
          value={data?.utm_source ?? ""}
          textToCopy={`https://get.halper.ai/get-app?utm_source=${data?.utm_source}`}
        />
      </div>

      {/* Charts (stacked, left) + Weekly Goal (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start pb-4">
        {/* 3 charts — stacked, one per row, each with its total */}
        <div className="lg:col-span-2 flex flex-col gap-4">
          <RevenueCommissions
            title={t("affiliate.clicks")}
            subtitle={t("affiliate.clicksSubtitle")}
            data={charts?.clicks ?? []}
            total={data?.clicks_total}
            color="#6366f1"
            gradientId="aff-clicks"
          />
          <RevenueCommissions
            title={t("affiliate.registeredUsers")}
            subtitle={t("affiliate.registeredUsersSubtitle")}
            data={charts?.registered_users_count ?? []}
            total={data?.registered_users_count_total}
            color="#10b981"
            gradientId="aff-users"
          />
          <RevenueCommissions
            title={t("affiliate.earnedMoney")}
            subtitle={t("affiliate.earnedMoneySubtitle")}
            data={charts?.earned_money ?? []}
            total={data ? Number(data.earned_money_total) : undefined}
            color="#f59e0b"
            valuePrefix={currencySymbol}
            gradientId="aff-money"
          />
        </div>

        {/* Weekly Goal — right column */}
        <WeeklyGoal />
      </div>
    </div>
  );
}
